import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { StyleSheet, ScrollView, RefreshControl, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PageSearchInput from '@/components/Inputs/PageSearchInput';

const ExplorePageContext = createContext(null);

export const createExplorePageStyles = (colors, isXS, isXL, hideHeaderBorder, insetBottom = 0) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
    },
    contentWrapper: {
      width: '100%',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      ...(Platform.OS === 'web' && { position: 'sticky' }),
      top: 0,
      zIndex: 10,
      backgroundColor: colors.solid_color_primary,
      width: '100%',
      borderBottomWidth: hideHeaderBorder ? 0 : 1,
      borderBottomColor: hideHeaderBorder ? 'transparent' : colors.border_color_secondary,
    },
    headerContent: {
      width: '100%',
      maxWidth: isXL ? 1000 : 768,
      paddingBottom: 15,
      alignSelf: 'center',
    },
    scrollContainer: {
      flex: 1,
      width: '100%',
    },
    scrollContainerContent: {
      width: '100%',
      paddingBottom: insetBottom + 40,
    },
    content: {
      paddingTop: 5,
      width: '100%',
      maxWidth: isXL ? 1000 : 768,
      alignSelf: 'center',
    },
    section: {
      marginBottom: isXS ? 25 : 40,
    },
    sectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: isXS ? 2 : 5,
      paddingHorizontal: isXS ? 12 : 24,
      marginBottom: isXS ? 8 : 16,
    },
    sectionTitleText: {
      color: colors.text_color_primary,
    },
    sectionTitleRow: {
      flex: 1,
    },
    sectionSubtitleText: {
      marginTop: 4,
      opacity: 0.85,
    },
  });

/**
 * Section block for ExplorePage. Use inside ExplorePage children.
 * Renders a titled section with consistent spacing.
 * @param {string} [subtitle] - Optional subtitle shown under the section title
 * @param {React.ReactNode} [headerRight] - Optional content rendered to the right of the title (e.g. carousel nav buttons)
 */
export function ExploreSection({ title, subtitle, headerRight, children }) {
  const ctx = useContext(ExplorePageContext);
  if (!ctx) {
    throw new Error('ExploreSection must be used inside ExplorePage');
  }
  const { styles } = ctx;
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <Text size="l" weight="medium" style={styles.sectionTitleText}>
            {title}
          </Text>
          {subtitle != null && (
            <Text size="s" color="secondary" style={styles.sectionSubtitleText}>
              {subtitle}
            </Text>
          )}
        </View>
        {headerRight != null ? headerRight : null}
      </View>
      {children}
    </View>
  );
}

/**
 * Shared explore layout for Images, Music, Videos pages.
 * Renders MetaHead, sticky header with PageSearchInput, and scrollable content with pull-to-refresh.
 *
 * @param {string} metaTitle - MetaHead title
 * @param {string} metaDescription - MetaHead description
 * @param {string} searchPlaceholder - Placeholder for the header search input
 * @param {string} searchIcon - Icon name for the search input (e.g. 'image', 'video', 'music')
 * @param {string} searchValue - Controlled value for the search input
 * @param {(text: string) => void} onSearchChange - Called when the search input value changes
 * @param {() => void} onSearchSubmit - Called when the user submits the search (e.g. Enter or submit button)
 * @param {() => Promise<void>} [onRefresh] - Pull-to-refresh handler
 * @param {React.ReactNode} children - Scroll content (use ExploreSection for each block)
 * @param {React.ReactNode} [footer] - Optional full-width content rendered at the
 *   bottom of the scroll, outside the centered content column (e.g. AppDashboardFooter)
 */
export function ExplorePage({
  metaTitle,
  metaDescription,
  metaCanonical,
  searchPlaceholder,
  searchIcon,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onRefresh,
  children,
  footer,
}) {
  const { colors } = useTheme();
  const { isXS, isXL } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const [refreshing, setRefreshing] = useState(false);
  const [isNearTop, setIsNearTop] = useState(true);

  const styles = useMemo(
    () => createExplorePageStyles(colors, isXS, isXL, isNearTop, insets.bottom),
    [colors, isXS, isXL, isNearTop, insets.bottom]
  );

  const handleRefresh = useCallback(async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  }, [onRefresh]);

  const contextValue = useMemo(() => ({ styles, colors }), [styles, colors]);

  return (
    <ExplorePageContext.Provider value={contextValue}>
      <View style={styles.container}>
        {/*
          Explore pages (images / music / videos) render the public Gab AI
          galleries — fully browseable by anon visitors, so they're SEO-
          indexable by default. Callers pass `metaCanonical` (e.g. "/images")
          when they want a stable URL signal.
        */}
        <MetaHead title={metaTitle} description={metaDescription} canonical={metaCanonical} />
        <View style={styles.contentWrapper}>
          <View style={styles.header}>
            <View style={styles.headerContent}>
              <PageSearchInput
                value={searchValue}
                onChangeText={onSearchChange}
                placeholder={searchPlaceholder}
                icon={searchIcon}
                onSubmit={onSearchSubmit}
                showSubmitButton
                isRoundedStyle={true}
              />
            </View>
          </View>

          <ScrollView
            style={styles.scrollContainer}
            contentContainerStyle={styles.scrollContainerContent}
            showsVerticalScrollIndicator={false}
            onScroll={(e) => setIsNearTop(e.nativeEvent.contentOffset.y < 10)}
            scrollEventThrottle={16}
            refreshControl={
              onRefresh ? (
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={handleRefresh}
                  tintColor={colors.text_color_secondary}
                  colors={[colors.text_color_secondary]}
                />
              ) : undefined
            }
          >
            <View style={styles.content}>{children}</View>
            {footer != null ? footer : null}
          </ScrollView>
        </View>
      </View>
    </ExplorePageContext.Provider>
  );
}

export default ExplorePage;
